import { RefObject } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { customerFlowNames } from 'src/components/CustomerId';

interface SavedFilter {
  key: string;
  label: string;
  value: string | string[];
}

interface ActiveFiltersRowProps {
  savedFilters: SavedFilter[];
  filtersRowCollapsed: boolean;
  filtersRowMeasuredHeight: number;
  filtersRowRef: RefObject<HTMLDivElement>;
  onToggleCollapse: () => void;
  onClearAll: () => void;
  onRemoveFilter: (key: string) => void;
  onRemoveFilterValue: (key: string, value: string) => void;
}

const formatValidationDisplay = (value: string) => value.replace(/_/g, ' ');

const isValidationFilter = (filter: SavedFilter) =>
  filter.key === 'validation' ||
  filter.key === 'result' ||
  filter.label === 'validation' ||
  filter.label === 'result';

const ActiveFiltersRow = ({
  savedFilters,
  filtersRowCollapsed,
  filtersRowMeasuredHeight,
  filtersRowRef,
  onToggleCollapse,
  onClearAll,
  onRemoveFilter,
  onRemoveFilterValue,
}: ActiveFiltersRowProps) => {
  if (savedFilters.length === 0) return null;

  return (
    <div className="mt-2">
      <div className="bg-white border rounded-md">
        <div className="flex items-center justify-between px-2 py-1">
          <div className="flex items-center gap-2">
            <button
              className="h-6 w-6 flex items-center justify-center rounded hover:bg-[#F7F9FC]"
              aria-label={filtersRowCollapsed ? 'Expand filters' : 'Collapse filters'}
              aria-expanded={!filtersRowCollapsed}
              onClick={onToggleCollapse}
            >
              {filtersRowCollapsed ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </button>
            <span className="text-xs font-semibold text-[#6B6B6B]">
              Active filters ({savedFilters.length})
            </span>
          </div>
          <button
            className="text-xs text-[#00387D] hover:underline px-2"
            onClick={onClearAll}
          >
            Clear all
          </button>
        </div>
        <div
          className="pb-2 overflow-hidden"
          style={{
            maxHeight: filtersRowCollapsed ? 0 : filtersRowMeasuredHeight,
            padding: filtersRowCollapsed ? '0 8px 0 8px' : '0 8px 8px 8px',
            transition: 'max-height 220ms ease, padding 220ms ease',
          }}
        >
          <div
            ref={filtersRowRef}
            className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1"
            style={{ whiteSpace: 'nowrap' }}
          >
            {savedFilters.map(f => {
              const isFlowIds = f.key === 'flowId' || f.key === 'flowIds';
              const isAssignee =
                f.key === 'assignedUserId' || f.key === 'assignedUserName';
              const assigneeValue =
                f.key === 'assignedUserName'
                  ? String(f.value)
                  : f.label &&
                      f.label !== 'Assignee' &&
                      f.label !== 'assignedUserId' &&
                      f.label !== 'assignedUserName'
                    ? f.label
                    : String(f.value);
              const isValidation = isValidationFilter(f);
              const isMultiValue =
                (Array.isArray(f.value) ||
                  (isValidation && typeof f.value === 'string')) &&
                (f.key === 'provider' ||
                  f.key === 'flowId' ||
                  f.key === 'flowIds' ||
                  f.key === 'validation' ||
                  f.key === 'result' ||
                  f.label === 'validation' ||
                  f.label === 'result' ||
                  f.key === 'status');
              const isCommaSeparatedFlowIds =
                isFlowIds &&
                typeof f.value === 'string' &&
                f.value.includes(',');
              const isSingleFlowId =
                isFlowIds &&
                typeof f.value === 'string' &&
                !f.value.includes(',');

              const displayValues = isCommaSeparatedFlowIds
                ? (f.value as string).split(',')
                : Array.isArray(f.value)
                ? f.value
                : isSingleFlowId
                ? [f.value]
                : isValidation && typeof f.value === 'string'
                ? [f.value]
                : null;

              const showOuterRemoveButton = !(
                (isMultiValue || isCommaSeparatedFlowIds || isSingleFlowId) &&
                displayValues &&
                displayValues.length >= 1
              );

              return (
                <span
                  key={f.key}
                  className="inline-flex items-center rounded-full bg-[#00387D] text-white border border-[#00387D] px-2 py-1 text-xs"
                >
                  <span className="font-semibold mr-1">
                    {isAssignee ? 'Assignee' : f.label}:
                  </span>
                  {isAssignee ? (
                    <span>{assigneeValue}</span>
                  ) : isMultiValue || isCommaSeparatedFlowIds || isSingleFlowId ? (
                    <span className="inline-flex gap-1 items-center">
                      {displayValues?.map(v => {
                        const displayValue = isFlowIds
                          ? (customerFlowNames as Record<string, string>)[v] || v
                          : isValidation
                          ? formatValidationDisplay(v)
                          : v;
                        return (
                          <span
                            key={v}
                            className="inline-flex items-center bg-[#00387D] text-white rounded px-1"
                            title={isFlowIds ? v : undefined}
                          >
                            <span>{displayValue}</span>
                            <button
                              className="ml-1 rounded-full hover:bg-[#00387D]/20 p-0.5"
                              aria-label={`Remove ${displayValue}`}
                              onClick={() => onRemoveFilterValue(f.key, v)}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        );
                      })}
                    </span>
                  ) : (
                    <span>
                      {Array.isArray(f.value)
                        ? f.value
                            .map(v => (isValidation ? formatValidationDisplay(v) : v))
                            .join(', ')
                        : isValidation
                        ? formatValidationDisplay(String(f.value))
                        : f.value}
                    </span>
                  )}
                  {showOuterRemoveButton && (
                    <button
                      className="ml-2 rounded-full hover:bg-[#00387D]/20 p-0.5"
                      aria-label={`Remove ${f.label}`}
                      onClick={() => onRemoveFilter(f.key)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveFiltersRow;
